/**
 * Reports module — typed report metadata.
 *
 * Reports are stored in `public.incidents` with `report_type` and a flexible
 * `report_data` JSONB payload. This file is the single source of truth for
 * the catalog of report types, their labels, icons, and role gates.
 */
import {
  AlertTriangle,
  Car,
  ClipboardList,
  Crosshair,
  HardHat,
  Wrench,
  ShieldAlert,
  Heart,
  Eye,
  UserX,
  Sunrise,
  Sunset,
  type LucideIcon,
} from "lucide-react";

export type ReportType =
  | "critical_incident"
  | "company_car_accident"
  | "equipment_damage"
  | "maintenance_issue"
  | "weapons_discharge"
  | "supplemental"
  | "use_of_force"
  | "medical_incident"
  | "suspicious_activity"
  | "visitor_removal"
  | "shift_start"
  | "shift_observation"
  | "shift_close_out";

export type ReportTypeMeta = {
  type: ReportType;
  label: string;
  short: string;
  description: string;
  icon: LucideIcon;
  /** Org roles allowed to create this report type. Empty = any member. */
  restrictedToRoles?: string[];
  /** When true, sets incidents.restricted = true (extra RLS gate). */
  restrictedRecord?: boolean;
  /** Default severity assigned at draft time. */
  defaultSeverity: "low" | "medium" | "high" | "critical";
  /** Default category in the existing incidents schema. */
  defaultCategory: string;
};

export const REPORT_TYPES: ReportTypeMeta[] = [
  {
    type: "critical_incident",
    label: "Critical Incident Report",
    short: "Critical Incident",
    description: "Major incident requiring leadership notification and escalation.",
    icon: AlertTriangle,
    defaultSeverity: "high",
    defaultCategory: "incident",
  },
  {
    type: "company_car_accident",
    label: "Company Car Accident",
    short: "Vehicle Accident",
    description: "Collision, damage, or incident involving a company vehicle.",
    icon: Car,
    defaultSeverity: "medium",
    defaultCategory: "incident",
  },
  {
    type: "equipment_damage",
    label: "Equipment Damage Report",
    short: "Equipment Damage",
    description: "Damaged or non-functional equipment, tools, or assets.",
    icon: HardHat,
    defaultSeverity: "low",
    defaultCategory: "incident",
  },
  {
    type: "maintenance_issue",
    label: "Maintenance Issue",
    short: "Maintenance",
    description: "Facility, building, or site maintenance items requiring attention.",
    icon: Wrench,
    defaultSeverity: "low",
    defaultCategory: "incident",
  },
  {
    type: "weapons_discharge",
    label: "Weapons Discharge Report",
    short: "Weapons Discharge",
    description: "Restricted — supervisors and admins only. Discharge of a firearm.",
    icon: Crosshair,
    restrictedToRoles: ["org_owner", "admin", "supervisor"],
    restrictedRecord: true,
    defaultSeverity: "critical",
    defaultCategory: "incident",
  },
  {
    type: "use_of_force",
    label: "Use of Force Report",
    short: "Use of Force",
    description: "Restricted — physical intervention, restraint, or force applied by personnel.",
    icon: ShieldAlert,
    restrictedToRoles: ["org_owner", "admin", "supervisor"],
    restrictedRecord: true,
    defaultSeverity: "high",
    defaultCategory: "incident",
  },
  {
    type: "medical_incident",
    label: "Medical Incident",
    short: "Medical",
    description: "Injury, illness, or medical event involving personnel, visitors, or public.",
    icon: Heart,
    defaultSeverity: "high",
    defaultCategory: "incident",
  },
  {
    type: "suspicious_activity",
    label: "Suspicious Activity",
    short: "Suspicious Activity",
    description: "Observed activity warranting documentation and review.",
    icon: Eye,
    defaultSeverity: "medium",
    defaultCategory: "incident",
  },
  {
    type: "visitor_removal",
    label: "Visitor Removal / Trespass",
    short: "Trespass",
    description: "Subject escorted off site, trespass warning issued, or no-trespass order.",
    icon: UserX,
    defaultSeverity: "medium",
    defaultCategory: "incident",
  },
  {
    type: "shift_start",
    label: "Shift Start Report",
    short: "Shift Start",
    description: "Begin of shift documentation — post conditions, equipment check, briefing notes.",
    icon: Sunrise,
    defaultSeverity: "low",
    defaultCategory: "shift_report",
  },
  {
    type: "shift_observation",
    label: "Hourly Shift Observation Report",
    short: "Shift Observation",
    description: "Log observations on post — activity, anomalies, weather, foot traffic.",
    icon: Eye,
    defaultSeverity: "low",
    defaultCategory: "shift_report",
  },
  {
    type: "shift_close_out",
    label: "Shift Close-Out Report",
    short: "Shift Close-Out",
    description: "End of shift summary and handoff notes for the relieving officer.",
    icon: Sunset,
    defaultSeverity: "low",
    defaultCategory: "shift_report",
  },
  {
    type: "supplemental",
    label: "Supplemental / Follow-Up Report",
    short: "Supplemental",
    description: "Continuation or follow-up linked to a previously filed report.",
    icon: ClipboardList,
    defaultSeverity: "low",
    defaultCategory: "follow_up",
  },
];

export const REPORT_TYPE_MAP: Record<ReportType, ReportTypeMeta> = REPORT_TYPES.reduce(
  (acc, m) => {
    acc[m.type] = m;
    return acc;
  },
  {} as Record<ReportType, ReportTypeMeta>,
);

const PRIVILEGED_ROLES = new Set(["org_owner", "admin", "supervisor", "dispatcher"]);
const MANAGEMENT_ROLES = new Set(["org_owner", "admin"]);

export function canCreateReport(meta: ReportTypeMeta, role: string | null): boolean {
  if (!role) return false;
  if (!meta.restrictedToRoles?.length) return true;
  return meta.restrictedToRoles.includes(role);
}

export function isPrivilegedRole(role: string | null): boolean {
  return !!role && PRIVILEGED_ROLES.has(role);
}

export function isManagementRole(role: string | null): boolean {
  return !!role && MANAGEMENT_ROLES.has(role);
}

/** "Reports" status flow on top of the existing incidents.status column. */
export const REPORT_STATUS_FLOW = [
  "draft",
  "open",
  "in_progress",
  "pending_review",
  "resolved",
  "closed",
] as const;

export type ReportStatus = (typeof REPORT_STATUS_FLOW)[number];
